import {
  offset,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import React, { ReactElement } from "react";

import { useFloatingTransition } from "../../hooks/useFloatingTransition";
import TooltipUi from "./TooltipUi";

interface TooltipProps extends React.ComponentProps<"div"> {
  /** Content of the Tooltip */
  label: string;
  /** Placement of tooltip relative to reference element */
  placement?: "top" | "bottom" | "left" | "right";
  /** Children elements */
  children: ReactElement;
}

/** Tooltip that renders. Note that components wrapped by `<Tooltip>` **must** either have
 * a `forwardRef` — the recommended way — or if that this not an option, an
 * HTML element like a `<span>`, as HTML elements accept a `forwardRef` by default. */

const Tooltip: React.FC<TooltipProps> = ({
  placement = "top",
  label,
  children,
}) => {
  // Setting up floating-ui configuration
  // -------------------------------------------------

  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    middleware: [offset(4)],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  // Interaction hooks for hover behavior
  // -------------------------------------------------

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  // Transition hooks for fade in/out
  // -------------------------------------------------

  const { isMounted, transitionStyles } = useFloatingTransition(context);

  const clonedChildren = React.cloneElement(children, {
    ref: refs.setReference,
    ...getReferenceProps(),
  });

  return (
    <>
      {clonedChildren}
      {isMounted && (
        <div
          style={{ ...floatingStyles, ...transitionStyles }}
          ref={refs.setFloating}
          {...getFloatingProps()}
        >
          <TooltipUi label={label} />
        </div>
      )}
    </>
  );
};

export default Tooltip;
